import { useState, useRef, useEffect } from 'react';
import {
    Box,
    Input,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
    HStack,
    Text,
    Button,
    IconButton,
    Icon,
    Grid,
    GridItem,
    Flex,
} from '@chakra-ui/react';
import { TbChevronLeft, TbChevronRight, TbCalendar } from 'react-icons/tb';

interface DatePickerProps {
    value: Date;
    onChange: (date: Date) => void;
    placeholder?: string;
    size?: 'sm' | 'md' | 'lg';
    fontSize?: string;
    flex?: string;
    color?: string;
    fontWeight?: string;
    borderless?: boolean;
}

const DatePicker = ({
    value,
    onChange,
    placeholder = 'Select date',
    size = 'sm',
    fontSize = 'sm',
    flex,
    color = 'gray.900',
    fontWeight = 'normal',
    borderless = false,
}: DatePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [displayDate, setDisplayDate] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setDisplayDate(value);
    }, [value]);

    const formatDateForDisplay = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const getDaysInMonth = (date: Date): number => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date): number => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setDisplayDate((prev) => {
            const newDate = new Date(prev);
            if (direction === 'prev') {
                newDate.setMonth(prev.getMonth() - 1);
            } else {
                newDate.setMonth(prev.getMonth() + 1);
            }
            return newDate;
        });
    };

    const selectDate = (day: number) => {
        const newDate = new Date(displayDate);
        newDate.setDate(day);
        onChange(newDate);
        setIsOpen(false);
    };

    const isToday = (day: number): boolean => {
        const today = new Date();
        return (
            day === today.getDate() &&
            displayDate.getMonth() === today.getMonth() &&
            displayDate.getFullYear() === today.getFullYear()
        );
    };

    const isSelected = (day: number): boolean => {
        return (
            day === value.getDate() &&
            displayDate.getMonth() === value.getMonth() &&
            displayDate.getFullYear() === value.getFullYear()
        );
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const daysInMonth = getDaysInMonth(displayDate);
    const firstDay = getFirstDayOfMonth(displayDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
        days.push(null);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
        days.push(day);
    }

    return (
        <Popover
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            placement="bottom"
            closeOnBlur={true}
        >
            <PopoverTrigger>
                <Box position="relative" flex={flex}>
                    <Input
                        ref={inputRef}
                        size={size}
                        type="text"
                        value={formatDateForDisplay(value)}
                        readOnly
                        placeholder={placeholder}
                        fontSize={fontSize}
                        fontWeight={fontWeight}
                        color={color}
                        cursor="pointer"
                        onClick={() => setIsOpen(true)}
                        pr="36px"
                        border={borderless ? "none" : undefined}
                        bg={borderless ? "transparent" : undefined}
                        _hover={borderless ? { bg: "transparent" } : undefined}
                        _focus={{
                            borderColor: borderless ? 'transparent' : '#0071E3',
                            boxShadow: borderless ? 'none' : '0 0 0 1px #0071E3',
                            bg: borderless ? 'transparent' : undefined,
                        }}
                        p={borderless ? 1 : undefined}
                    />
                    <Box
                        position="absolute"
                        right="8px"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                    >
                        <Icon as={TbCalendar as React.ElementType} w={4} h={4} />
                    </Box>
                </Box>
            </PopoverTrigger>
            <PopoverContent
                w="320px"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                boxShadow="none"
                p={0}
                _focus={{ boxShadow: 'none' }}
            >
                <PopoverBody p={4}>
                    <VStack spacing={4} align="stretch">
                        {/* Header with month/year and navigation */}
                        <Flex justify="center" align="center">
                            <HStack spacing={2}>
                                <IconButton
                                    aria-label="Previous month"
                                    icon={<Icon as={TbChevronLeft as React.ElementType} />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigateMonth('prev')}
                                    color="gray.700"
                                    _hover={{
                                        bg: '#E6F1FD',
                                        color: '#0071E3',
                                    }}
                                />
                                <Text
                                    fontSize="md"
                                    fontWeight="semibold"
                                    color="gray.900"
                                    minW="140px"
                                    textAlign="center"
                                >
                                    {monthNames[displayDate.getMonth()]} {displayDate.getFullYear()}
                                </Text>
                                <IconButton
                                    aria-label="Next month"
                                    icon={<Icon as={TbChevronRight as React.ElementType} />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigateMonth('next')}
                                    color="gray.700"
                                    _hover={{
                                        bg: '#E6F1FD',
                                        color: '#0071E3',
                                    }}
                                />
                            </HStack>
                        </Flex>

                        {/* Day names header */}
                        <Grid templateColumns="repeat(7, 1fr)" gap={1}>
                            {dayNames.map((day) => (
                                <GridItem key={day}>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="gray.600"
                                        textAlign="center"
                                        py={2}
                                    >
                                        {day}
                                    </Text>
                                </GridItem>
                            ))}
                        </Grid>

                        {/* Calendar grid */}
                        <Grid templateColumns="repeat(7, 1fr)" gap={1}>
                            {days.map((day, index) => {
                                if (day === null) {
                                    return <GridItem key={`empty-${index}`} />;
                                }

                                const isTodayDate = isToday(day);
                                const isSelectedDate = isSelected(day);

                                return (
                                    <GridItem key={day}>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            w="100%"
                                            h="36px"
                                            p={0}
                                            fontSize="sm"
                                            fontWeight={isSelectedDate ? 'semibold' : 'normal'}
                                            bg={
                                                isSelectedDate
                                                    ? '#0071E3'
                                                    : isTodayDate
                                                        ? '#E6F1FD'
                                                        : 'transparent'
                                            }
                                            color={
                                                isSelectedDate
                                                    ? 'white'
                                                    : isTodayDate
                                                        ? '#0071E3'
                                                        : 'gray.700'
                                            }
                                            _hover={{
                                                bg: isSelectedDate ? '#005BC4' : '#E6F1FD',
                                                color: isSelectedDate ? 'white' : '#0071E3',
                                            }}
                                            _active={{
                                                bg: isSelectedDate ? '#00469B' : '#E6F1FD',
                                            }}
                                            onClick={() => selectDate(day)}
                                            borderRadius="md"
                                        >
                                            {day}
                                        </Button>
                                    </GridItem>
                                );
                            })}
                        </Grid>

                        {/* Today button */}
                        <Flex justify="center" pt={2} borderTop="1px solid" borderColor="gray.200">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    const today = new Date();
                                    onChange(today);
                                    setDisplayDate(today);
                                    setIsOpen(false);
                                }}
                                color="#0071E3"
                                fontWeight="medium"
                                _hover={{
                                    bg: '#E6F1FD',
                                }}
                            >
                                Today
                            </Button>
                        </Flex>
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default DatePicker;

